import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { AuthService } from '@nomoh-monorepo/data-access-auth';
import {
  IContextoBardo,
  PerfilAcessoBardo,
} from '@nomoh-monorepo/shared-types';
import { obterPermissoesPorPerfilBardo } from '@nomoh-monorepo/shared-util-tools';

const STORAGE_KEY = 'bardo_contexto_ativo';

/**
 * O JWT com estabelecimento grava o id do estabelecimento (ou do profissional) no contexto.
 * Sem estabelecimento, o login de onboarding grava o id da pessoa.
 */
export function temEstabelecimentoNaSessao(
  contexto: IContextoBardo | null,
  pessoaId: string | undefined
): boolean {
  return !!contexto && !!pessoaId && contexto.contextoId !== pessoaId;
}

@Injectable({ providedIn: 'root' })
export class BardoContextDataService {
  private _contextoAtivo = signal<IContextoBardo | null>(this._fromStorage());

  $contextoAtivo = this._contextoAtivo.asReadonly();

  $perfilAtivo = computed((): PerfilAcessoBardo => {
    const ctx = this._contextoAtivo();
    return ctx?.papel ?? 'PROFISSIONAL';
  });

  /** ID do profissional vinculado ao usuário logado (só existe quando papel === 'PROFISSIONAL'). */
  $meuProfissionalId = computed((): string | null => {
    const ctx = this._contextoAtivo();
    return ctx?.papel === 'PROFISSIONAL' ? ctx.profissionalId ?? null : null;
  });

  /**
   * ID do profissional vinculado ao usuário logado, independente do
   * perfilAcesso (PROFISSIONAL, RECEPCAO ou ADMINISTRADOR-funcionário) —
   * existe sempre que o contexto for de um funcionário (tipo === 'PROFISSIONAL'),
   * já que o backend preenche profissionalId nesse caso (ver bardo-auth.service.ts).
   * Diferente de `$meuProfissionalId`, que é restrito ao papel PROFISSIONAL e é
   * usado para limitar a tela de novo agendamento ao próprio profissional — não
   * reaproveitar lá. Criado para a Recepção híbrida consultar o próprio
   * fechamento (aprovado 2026-07-10 — docs/perfil/REFINAMENTO_SERVICOS_OPCIONAIS_RECEPCAO.md).
   */
  $profissionalIdVinculado = computed((): string | null => {
    const ctx = this._contextoAtivo();
    return ctx?.tipo === 'PROFISSIONAL' ? ctx.profissionalId ?? null : null;
  });

  constructor() {
    const authService = inject(AuthService);
    effect(() => {
      const authCtx = authService.$bardoContextoAtivo();
      if (authCtx) {
        this.setContextoAtivo(authCtx);
      }
    });
  }

  setContextoAtivo(contexto: IContextoBardo): void {
    this._contextoAtivo.set(contexto);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(contexto));
  }

  clearContexto(): void {
    this._contextoAtivo.set(null);
    localStorage.removeItem(STORAGE_KEY);
  }

  private _fromStorage(): IContextoBardo | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as IContextoBardo) : null;
    } catch {
      return null;
    }
  }

  obterPermissoesPorPerfil(perfilAcesso: string): string[] {
    return obterPermissoesPorPerfilBardo(perfilAcesso);
  }

  possuiPermissao(permissao: string): boolean {
    const ctx = this._contextoAtivo();
    if (!ctx) return false;

    // DONO tem acesso total
    if (ctx.tipo === 'DONO') return true;

    const papel = ctx.papel;
    
    // ADMINISTRADOR tem acesso total
    if (papel === 'ADMINISTRADOR') return true;
    
    const permissoesDoPerfil = this.obterPermissoesPorPerfil(papel);
    
    // Fallback: mapeamentos legados no front
    const isAgendamentosCreate = permissao === 'Bardo.agendamentos.create' && permissoesDoPerfil.includes('Bardo.agendas.create');
    const isAgendamentosUpdate = permissao === 'Bardo.agendamentos.update' && permissoesDoPerfil.includes('Bardo.agendas.update');
    const isAgendamentosDelete = permissao === 'Bardo.agendamentos.delete' && permissoesDoPerfil.includes('Bardo.agendas.delete');

    return permissoesDoPerfil.includes(permissao) || isAgendamentosCreate || isAgendamentosUpdate || isAgendamentosDelete;
  }
}
